import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
    SidebarNav,
    SidebarNavItem
} from '@helix/sidebar-nav';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { launch } from '@lwt-helix/helix-icon/outlined';
import { routesToHideNavBar } from '../../../constants/nav';
import { moduleRoutes } from '../../../constants';
import { useCommonStore, useSearchStore } from '../../../store/store';

// The ApplicationLayout wraps the entire application with the Helix Sidebar Navigation,
// it provides the navigation structure for both XBM and XAM users.
const ApplicationLayout = ({ children, navItems }) => {
    const location = useLocation();
    const [sidebarNavItems, setSidebarNavItems] = useState([]);
    const sidebarContainerRef = useRef(null);

    const selectedModule = useSearchStore(state => state.selectedModule);
    const setIsSideNavExpanded = useCommonStore(state => state.setIsSideNavExpanded);

    useEffect(() => {
        const container = sidebarContainerRef.current;
        if (!container) return;

        const observer = new MutationObserver(() => {
            const navContainer = container.querySelector('[class*="sidebar-nav-container"]');
            if (!navContainer) return;
            const isCollapsed = [...navContainer.classList].some(c => c.includes('collapsed'));
            setIsSideNavExpanded(!isCollapsed);
        });

        observer.observe(container, { subtree: true, attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const shouldShowNavBar = !routesToHideNavBar.includes(location.pathname);

    // Convert nav items to sidebar nav items format, flatten all items with headers and indentation.
    useEffect(() => {
        if (!navItems) return;
        const convertedItems = [];
        const excludeHeaders = ['Dashboard', 'Agent Production'];

        navItems.forEach((item, index) => {
            if (!item) return;

            // Add the section header only if not excluded
            if (!excludeHeaders.includes(item.label)) {
                convertedItems.push({
                    id: `header-${index}`,
                    text: item.label,
                    isHeader: true
                });
            }

            if (item.menuItems) {
                // Add all children with their indentation
                item.menuItems.forEach((menuItem, menuIndex) => {
                    if (!menuItem.header) {
                        // Regular menu items
                        convertedItems.push({
                            id: `item-${index}-${menuIndex}`,
                            text: menuItem.label,
                            onClick: menuItem.onClick,
                            url: menuItem.url,
                            target: menuItem.target,
                            active: location.pathname === menuItem.activePathname
                                || moduleRoutes[selectedModule] === menuItem.activePathname,
                            isChild: !excludeHeaders.includes(item.label)
                        });
                    }
                });
            } else {
                // Single nav item, add as child under its own header (if header was added)
                convertedItems.push({
                    id: `item-${index}`,
                    text: item.label,
                    onClick: item.onClick,
                    active: location.pathname === item.activePathname
                        || moduleRoutes[selectedModule] === item.activePathname,
                    isChild: !excludeHeaders.includes(item.label)
                });
            }
        });

        setSidebarNavItems(convertedItems);
    }, [navItems, location.pathname, selectedModule]);

    if (!shouldShowNavBar) {
        return <>{children ?? null}</>;
    }

    const renderSideNavSectionHeader = (item) => (
        <div
            key={item.id}
            className='sidebar-nav-section-header helix-text--sm helix-text--bold helix-text--uppercase'
            style={{ marginTop: '10px' }}
        >
            {item.text}
        </div>
    );

    const renderSideNavItem = (item) => (
        <SidebarNavItem
            key={item.id}
            active={item.active}
            className={item.isChild ? 'sidebar-nav-child-item' : ''}
        >
            {item.url ? (
                <a href={item.url} target={item.target} rel="noopener noreferrer" className='sidebar-nav-external-link'>
                    <span>{item.text}</span>
                    <HelixIcon icon={launch} className='sidebar-nav-external-link-icon' />
                </a>
            ) : (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        item.onClick && item.onClick();
                    }}
                >
                    {item.text}
                </a>
            )}
        </SidebarNavItem>
    );

    return (
        <div className='helix-d-flex helix-flex-1 helix-overflow--hidden'>
            {sidebarNavItems.length > 0 && (
                <div className='sidebar-container' ref={sidebarContainerRef}>
                    <SidebarNav>
                        {sidebarNavItems.map((item) => {
                            return item.isHeader ? renderSideNavSectionHeader(item) : renderSideNavItem(item);
                        })}
                    </SidebarNav>
                </div>
            )}

            <div className='helix-flex-1 helix-d-flex helix-flex-direction--column helix-overflow--auto application-content'>
                {children ?? null}
            </div>
        </div>
    );
};

ApplicationLayout.propTypes = {
    children: PropTypes.node,
    navItems: PropTypes.array
};

export default ApplicationLayout;
